import { ErrorEntity } from "@/lib/utils/error-entities";
import { Error as ErrorI } from "@/util-components/root/context";

const hasStoredAuthSessionHint = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return Object.keys(window.localStorage).some((key) => key.includes("@@auth0spajs@@") || key.startsWith("a0.spajs"));
  } catch {
    return false;
  }
};

const isUserInfoRequestFailure = (
  res: Response,
  error: Partial<ErrorI> & { traceId?: string; correlationId?: string },
) => {
  const isUserInfoRequest = res.url.includes("/user-profile/userinfo");
  const backendMessage = typeof error.message === "string" ? error.message : "";

  return isUserInfoRequest && backendMessage === "An error occured fetching userinfo!";
};

export const getErrorMessage = async (res: Response) => {
  let errorMessage = "";
  let errorLocation = "";
  let error: Partial<ErrorI> & { traceId?: string; correlationId?: string };
  let isClosable = true;
  let withReload = false;
  let title = "Es ist ein Fehler aufgetreten";
  let redirect = false;
  let traceId = res.headers.get("x-trace-id") ?? undefined;
  let correlationId = res.headers.get("x-correlation-id") ?? undefined;

  try {
    error = (await res.json()) as Partial<ErrorI> & { traceId?: string; correlationId?: string };
  } catch {
    error = {};
  }

  traceId = traceId ?? error.traceId;
  correlationId = correlationId ?? error.correlationId;
  const rawBackendMessage = Array.isArray(error.message) ? error.message.map((entry) => String(entry)).join(", ") : error.message;

  if (res.status === 400) {
    errorMessage = "400 - Scheinbar scheinen Ihre Angaben nicht ganz korrekt zu sein. Bitte korrigiere das und versuche es nochmals.";
  } else if (res.status === 409) {
    errorMessage = "409 - Scheinbar gab es einen Konflikt. Bitte versuche es später nochmals oder kontaktiere den Support.";
  } else if (res.status === 403) {
    errorMessage = "403 - Du scheinst nicht die nötigen Rechte zu haben. Bitte kontaktiere den Support oder Ihren Vereinsadministrator.";
  } else if (res.status === 500) {
    errorMessage = "500 - Es ist ein Fehler aufgetreten, der nicht auftreten soll. Bitte schreibe dem Support. - Zeitpunkt: " + new Date().toLocaleString();
  } else if (res.status === 404) {
    errorMessage = "404 - das Objekt wurde nicht gefunden. Bitte versuche es später nochmals oder kontaktiere den Support";
  } else {
    errorMessage = `${res.status} - Es ist ein Fehler aufgetreten. Bitte versuche es später nochmals oder kontaktiere den Support`;
  }

  if (error.error === ErrorEntity.NoUserFoundForbidden) {
    errorLocation = "/user-not-found";
    error.entity = ErrorEntity.NoUserFoundForbidden;
    redirect = true;
  } else if (isUserInfoRequestFailure(res, error)) {
    const hasStoredSession = hasStoredAuthSessionHint();

    errorLocation = hasStoredSession ? "/user-not-found" : "/";
    error.entity = hasStoredSession ? ErrorEntity.NoUserFoundForbidden : error.entity;
    redirect = true;
  } else if (error.error === ErrorEntity.EMailNotVerifiedForbidden) {
    errorLocation = "/verification/mail";
    error.entity = ErrorEntity.EMailNotVerifiedForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.NoOrganizationForbidden) {
    errorLocation = "/vereine/create";
    error.entity = ErrorEntity.NoOrganizationForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.NoBillingContractForbidden) {
    errorLocation = "/choose-plan";
    error.entity = ErrorEntity.NoBillingContractForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.BannedByAdminForbidden) {
    errorLocation = "/banned";
    error.entity = ErrorEntity.BannedByAdminForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.IncompleteProfileForbidden) {
    errorLocation = "/complete-profile";
    error.entity = ErrorEntity.IncompleteProfileForbidden;
    redirect = true;
  } else if (error.message === "Team is getting used") {
    errorMessage = "Das Team hat bereits Spieler zugewiesen. Entferne alle zugewiesenen Spieler und versuche es erneut.";
  }

  const parsedError: ErrorI = {
    message: errorMessage,
    error: error.error || "UnknownError",
    statusCode: res.status,
    backendMessage: typeof rawBackendMessage === "string" ? rawBackendMessage : undefined,
    metaData: error.metaData,
    metaStatusCode: typeof error.metaStatusCode === "number" ? error.metaStatusCode : undefined,
    response: {
      status: res.status,
      data: error,
    },
    entity: error.entity,
    location: {
      path: errorLocation,
      redirect,
    },
    isClosable,
    withReload,
    title,
    mail: error.mail,
    traceId,
    correlationId,
  };

  return parsedError
}
